/**
 * The two things that stand between a bulk action and a regret.
 *
 * Both are rendered from `model/selection.ts`, which has held the copy and the
 * rules since before the client existed. Nothing here decides anything: the
 * banner shows `escalationPrompt`'s words and the dialog shows
 * `confirmationFor`'s, verbatim. That matters because both have properties the
 * wording carries and a paraphrase would lose — see the note on `blocked`.
 */
import { useEffect, useRef, useState } from 'react'
import type { Confirmation, EscalationPrompt } from './model/selection.ts'

/**
 * The banner after `* a`.
 *
 * The offer is a BOUND when the count is inexact — "more than 10,000" — and the
 * model already phrases it that way, so this must not reformat it. An offer
 * reading "Select all 10,000" when the truth is 41,000 is a lie the reader then
 * acts on.
 */
export function EscalationBanner({
  prompt,
  escalated,
  onEscalate,
  onClear,
}: {
  prompt: EscalationPrompt | null
  escalated: boolean
  onEscalate: () => void
  onClear: () => void
}): React.JSX.Element | null {
  if (!prompt) return null
  return (
    <div className="escalation" role="status">
      {escalated ? (
        <>
          <span>
            {prompt.exact
              ? `All ${prompt.total.toLocaleString()} conversations that match this search are selected.`
              : `Every conversation that matches this search is selected — more than ${prompt.total.toLocaleString()}.`}
          </span>
          <button type="button" onClick={onClear}>
            Clear selection
          </button>
        </>
      ) : (
        <>
          <span>{prompt.message}</span>
          <button type="button" onClick={onEscalate}>
            {prompt.offer}
          </button>
        </>
      )}
    </div>
  )
}

/**
 * The dialog for a destructive action on an escalated set.
 *
 * THE 750 ms DISABLE IS THE POINT OF THIS COMPONENT. `interaction.md` requires
 * the destructive button to be inert for 750 ms after paint, so a keystroke
 * already in flight — the `#` that opened this dialog, held a moment too long,
 * or an Enter aimed at the list — cannot land on it. Without that there is a
 * muscle-memory path straight to deleting forty thousand messages.
 *
 * Cancel is on the LEFT, has default focus, and owns Enter. The destructive
 * button is on the right and owns nothing.
 *
 * `blocked` IS A DIFFERENT DIALOG, not a disabled button. It means the count was
 * capped rather than finished, so there is no honest number to put in the
 * sentence — and the model's message deliberately contains no digits at all. Its
 * only action is to go and count exactly; offering "yes" under a sentence that
 * refuses to name a figure would be worse than not asking.
 */
const ARMING_MS = 750

export function ConfirmDialog({
  confirmation,
  action,
  onConfirm,
  onCancel,
  onCountExactly,
}: {
  confirmation: Confirmation | null
  action: string
  onConfirm: () => void
  onCancel: () => void
  onCountExactly: () => void
}): React.JSX.Element | null {
  const [armed, setArmed] = useState(false)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const open = confirmation?.required === true

  useEffect(() => {
    if (!open) {
      setArmed(false)
      return
    }
    cancelRef.current?.focus()
    const timer = setTimeout(() => setArmed(true), ARMING_MS)
    return () => clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        onCancel()
      }
    }
    // Capture, so the shell's own Escape ladder does not act on the same press.
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onCancel])

  if (!open || !confirmation) return null

  return (
    <div className="modal-scrim" onClick={onCancel}>
      <div
        className="modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="confirm-title" className="modal-title">
          {confirmation.blocked ? 'Hearth cannot confirm that count' : `${action}?`}
        </h2>
        <p className="modal-body">{confirmation.message}</p>
        <div className="modal-actions">
          <button ref={cancelRef} type="button" className="modal-cancel" onClick={onCancel}>
            Cancel
          </button>
          {confirmation.blocked ? (
            <button type="button" className="modal-secondary" onClick={onCountExactly}>
              Count them exactly
            </button>
          ) : (
            <button
              type="button"
              className="modal-destructive"
              disabled={!armed}
              onClick={onConfirm}
              // Never the default action, and never reachable by Enter: the
              // focused Cancel owns that.
              tabIndex={0}
            >
              {armed ? action : 'Please wait…'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
